// ═══════════════════════════════════════════════════════════════
// Incident Hub — Incident Detail
// Full detail view: info grid, module launcher, activity log
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Badge, Button, SimpleGrid, Stack, Modal,
} from '@mantine/core';
import {
  IconArrowLeft, IconPencil, IconTrash, IconShield,
  IconCloudStorm, IconFlame, IconBiohazard, IconClipboardCheck,
  IconSchool, IconInfoCircle, IconDroplet, IconBuildingBridge,
  IconTornado, IconCalendar, IconAmbulance, IconConfetti,
  IconArrowsExchange, IconMap2, IconBuildingCommunity, IconUserPlus,
  IconCalendarEvent, IconAntenna, IconFolderOpen, IconChalkboard,
  IconCirclePlus, IconRefresh, IconCircleMinus, IconLogin,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useAuthStore } from '../../store/authStore';
import { useIncidentStore } from '../../store/incidentStore';
import { MODULES } from '../../lib/modules';
import { INCIDENT_TYPE_META } from '../../types';
import type { IncidentStatus } from '../../types';
import { primary, secondary, semantic } from '../../lib/colors';

const STATUS_COLORS: Record<string, string> = {
  active: semantic.warning,
  monitoring: semantic.info,
  closed: semantic.success,
  archived: secondary[700],
};

const ICON_MAP: Record<string, React.ComponentType<{ size?: number; color?: string; stroke?: number }>> = {
  IconCloudStorm, IconFlame, IconBiohazard, IconClipboardCheck,
  IconSchool, IconInfoCircle, IconDroplet, IconBuildingBridge,
  IconTornado, IconCalendar, IconShield, IconAmbulance, IconConfetti,
};

const MODULE_ICON_MAP: Record<string, React.ComponentType<{ size?: number; stroke?: number }>> = {
  IconFlame, IconArrowsExchange, IconClipboardCheck, IconMap2,
  IconBuildingCommunity, IconUserPlus, IconCalendarEvent, IconAntenna,
  IconFolderOpen, IconChalkboard,
};

const ACTION_ICONS: Record<string, { icon: React.ComponentType<{ size?: number; color?: string }>; color: string }> = {
  create: { icon: IconCirclePlus, color: '#07963F' },
  update: { icon: IconRefresh, color: primary[700] },
  delete: { icon: IconCircleMinus, color: semantic.error },
  check_in: { icon: IconLogin, color: '#16a34a' },
};

export default function IncidentDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { incidents, setActiveIncident, hasRight } = useAuthStore();
  const { activityLog, loadActivityLog, updateIncident, deleteIncident, isLoading } = useIncidentStore();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const incident = useMemo(
    () => incidents.find(i => String(i.id) === id),
    [incidents, id]
  );

  useEffect(() => {
    if (id) loadActivityLog(id);
  }, [id]);

  if (!incident) {
    return (
      <div>
        <Button variant="subtle" size="xs" leftSection={<IconArrowLeft size={14} />}
          onClick={() => navigate('/incidents')}>Back</Button>
        <Paper p="xl" radius="sm" withBorder mt="md">
          <Text ta="center" c="dimmed">Incident not found.</Text>
        </Paper>
      </div>
    );
  }

  const meta = INCIDENT_TYPE_META[incident.incidentType] || INCIDENT_TYPE_META.other;
  const TypeIcon = ICON_MAP[meta.icon] || IconInfoCircle;

  const handleStatusChange = async (newStatus: IncidentStatus) => {
    try {
      await updateIncident(incident.id, { status: newStatus });
      notifications.show({ message: `Status changed to ${newStatus}`, color: 'green' });
    } catch (err: any) {
      notifications.show({ message: err?.message || 'Failed to update status', color: 'red' });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteIncident(incident.id);
      notifications.show({ message: 'Incident deleted', color: 'gray' });
      navigate('/incidents');
    } catch (err: any) {
      notifications.show({ message: err?.message || 'Failed to delete', color: 'red' });
    }
  };

  const handleSetActive = () => {
    setActiveIncident(incident);
    notifications.show({ message: `${incident.name} set as active incident`, color: 'indigo' });
  };

  const incidentActivity = useMemo(
    () => [...activityLog].sort((a, b) => (b.datecreated || '').localeCompare(a.datecreated || '')).slice(0, 30),
    [activityLog]
  );

  return (
    <div>
      {/* Header */}
      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <Button variant="subtle" size="xs" leftSection={<IconArrowLeft size={14} />}
            onClick={() => navigate('/incidents')}>Back</Button>
        </Group>
        <Group gap="xs">
          <Button variant="light" size="xs" color="indigo" onClick={handleSetActive}>
            Set as Active
          </Button>
          <Button variant="light" size="xs" leftSection={<IconPencil size={14} />}
            onClick={() => navigate(`/incidents/${id}/edit`)}>Edit</Button>
          <Button variant="light" size="xs" color="red" leftSection={<IconTrash size={14} />}
            onClick={() => setShowDeleteModal(true)}>Delete</Button>
        </Group>
      </Group>

      {/* Title row */}
      <Group gap="sm" mb="lg">
        <div style={{
          width: 48, height: 48, borderRadius: 12,
          background: meta.color + '18', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
        }}>
          <TypeIcon size={24} color={meta.color} />
        </div>
        <div>
          <Group gap="xs">
            <Title order={3}>{incident.name}</Title>
            <Badge variant="light" size="lg" style={{
              backgroundColor: STATUS_COLORS[incident.status] + '18',
              color: STATUS_COLORS[incident.status],
            }}>{incident.status}</Badge>
          </Group>
          <Text size="xs" c="dimmed">
            {meta.label} · Created {incident.datecreated ? new Date(incident.datecreated).toLocaleDateString() : '—'} by {incident.createdByName || 'Unknown'}
          </Text>
        </div>
      </Group>

      {/* Status quick-change */}
      <Paper p="sm" radius="sm" withBorder mb="md">
        <Group gap="xs">
          <Text size="xs" fw={600} c="dimmed" mr="xs">Quick Status:</Text>
          {(['active', 'monitoring', 'closed'] as IncidentStatus[]).map(s => (
            <Button key={s} size="xs" variant={incident.status === s ? 'filled' : 'light'}
              color={s === 'active' ? 'yellow' : s === 'monitoring' ? 'cyan' : 'gray'}
              onClick={() => handleStatusChange(s)} loading={isLoading}
              disabled={incident.status === s}>
              {s}
            </Button>
          ))}
        </Group>
      </Paper>

      {/* Detail grid */}
      <SimpleGrid cols={{ base: 1, sm: 2 }} mb="lg">
        <Paper p="md" radius="sm" withBorder>
          <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={4}>Incident Commander</Text>
          <Text size="sm" fw={600}>{incident.commander || '—'}</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder>
          <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={4}>Location</Text>
          <Text size="sm" fw={600}>{incident.location || '—'}</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder>
          <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={4}>Start Date</Text>
          <Text size="sm" fw={600}>{incident.startDate || '—'}</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder>
          <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={4}>Type</Text>
          <Text size="sm" fw={600}>{meta.label}</Text>
        </Paper>
        {(incident.latitude || incident.longitude) && (
          <Paper p="md" radius="sm" withBorder>
            <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={4}>Coordinates</Text>
            <Text size="sm" fw={600} ff="monospace">{incident.latitude}, {incident.longitude}</Text>
          </Paper>
        )}
      </SimpleGrid>

      {/* Description */}
      {incident.description && (
        <Paper p="md" radius="sm" withBorder mb="lg">
          <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={4}>Description</Text>
          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{incident.description}</Text>
        </Paper>
      )}

      {/* Module Launcher */}
      <Text fw={600} size="sm" mb="sm">Launch Module for this Incident</Text>
      <SimpleGrid cols={{ base: 2, sm: 3, lg: 4 }} mb="lg">
        {MODULES.map(mod => {
          const ModIcon = MODULE_ICON_MAP[mod.icon];
          if (!hasRight(mod.tables[0], 'view')) return null;
          return (
            <Paper key={mod.id} p="sm" radius="sm" withBorder
              style={{ cursor: 'pointer', borderTopColor: mod.color, borderTopWidth: 3 }}
              onClick={() => navigate(mod.route)}>
              <Group gap="xs" mb={4}>
                {ModIcon && <ModIcon size={16} stroke={1.5} />}
                <Text size="sm" fw={700}>{mod.name}</Text>
              </Group>
              <Text size="xs" c="dimmed" lineClamp={1}>{mod.description}</Text>
            </Paper>
          );
        })}
      </SimpleGrid>

      {/* Activity Log */}
      <Text fw={600} size="sm" mb="sm">Incident Activity</Text>
      {incidentActivity.length === 0 ? (
        <Paper p="md" radius="sm" withBorder>
          <Text size="sm" c="dimmed" ta="center">No activity recorded yet.</Text>
        </Paper>
      ) : (
        <Stack gap={4}>
          {incidentActivity.map(entry => {
            const actionMeta = ACTION_ICONS[entry.action] || ACTION_ICONS.update;
            const ActionIcon = actionMeta.icon;
            let details = '';
            try { details = entry.details ? JSON.parse(entry.details)?.name || JSON.parse(entry.details)?.fields || '' : ''; } catch { details = entry.details; }
            return (
              <Paper key={entry.id} p="xs" radius="sm" withBorder>
                <Group gap="xs">
                  <ActionIcon size={14} color={actionMeta.color} />
                  <Text size="xs" fw={600}>{entry.actorName || 'System'}</Text>
                  <Text size="xs" c="dimmed">{entry.action} {entry.targetType}</Text>
                  {entry.module && entry.module !== 'hub' && (
                    <Badge size="xs" variant="light">{entry.module}</Badge>
                  )}
                  {details && <Text size="xs" c="dimmed" truncate style={{ flex: 1 }}>— {details}</Text>}
                  <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                    {entry.datecreated ? new Date(entry.datecreated).toLocaleString() : ''}
                  </Text>
                </Group>
              </Paper>
            );
          })}
        </Stack>
      )}

      {/* Delete Modal */}
      <Modal opened={showDeleteModal} onClose={() => setShowDeleteModal(false)}
        title="Delete Incident" centered size="sm">
        <Text size="sm" mb="md">
          Are you sure you want to delete <strong>{incident.name}</strong>? This cannot be undone.
        </Text>
        <Group justify="flex-end">
          <Button variant="light" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
          <Button color="red" onClick={handleDelete} loading={isLoading}>Delete</Button>
        </Group>
      </Modal>
    </div>
  );
}
